import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface DriverCheckResult {
  isDriver: boolean;
  isLoading: boolean;
  isDeactivated: boolean;
  isAdmin: boolean;
  deactivationReason?: string;
}

export const useDriverCheck = (): DriverCheckResult => {
  const { user, loading: authLoading } = useAuth();

  const { data, isLoading: queryLoading } = useQuery({
    queryKey: ['driver-check', user?.id],
    queryFn: async () => {
      if (!user?.id) return { isDriver: false, isDeactivated: false, isAdmin: false };
      
      // Check admin role and driver status in parallel
      const [adminResult, activeDriverResult] = await Promise.all([
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle(),
        supabase
          .from('drivers')
          .select('id')
          .eq('user_id', user.id)
          .eq('active', true)
          .maybeSingle(),
      ]);

      const isAdmin = !!adminResult.data;

      if (activeDriverResult.error) {
        console.error('Driver check error:', activeDriverResult.error);
        return { isDriver: false, isDeactivated: false, isAdmin };
      }
      
      if (activeDriverResult.data) {
        return { isDriver: true, isDeactivated: false, isAdmin };
      }

      // Check if there's an INACTIVE driver record (deactivated)
      const { data: inactiveDriver, error: inactiveError } = await supabase
        .from('drivers')
        .select('id, termination_reason')
        .eq('user_id', user.id)
        .eq('active', false)
        .maybeSingle();

      if (inactiveError) {
        console.error('Inactive driver check error:', inactiveError);
        return { isDriver: false, isDeactivated: false, isAdmin };
      }

      if (inactiveDriver) {
        return { 
          isDriver: false, 
          isDeactivated: true, 
          isAdmin,
          deactivationReason: inactiveDriver.termination_reason || undefined 
        };
      }

      return { isDriver: false, isDeactivated: false, isAdmin };
    },
    enabled: !!user?.id && !authLoading,
    staleTime: 300000,
    gcTime: 600000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const isLoading = authLoading || (!!user?.id && queryLoading);

  return { 
    isDriver: data?.isDriver ?? false, 
    isLoading,
    isDeactivated: data?.isDeactivated ?? false,
    isAdmin: data?.isAdmin ?? false,
    deactivationReason: data?.deactivationReason,
  };
};
